import { useEffect, useMemo, useState } from "react";

import type { DataSource, SourceEntity } from "../types/dataVault.types";
import { useAuth } from "../Contexts/AuthContext";
import { getScopeEnvironmentId } from "../services/activeScope";

const API_BASE =
  (process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/$/, "") : "") || "";

const buildUrl = (path: string) => {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  const normalizedPath = path.replace(/^\/+/, "");
  if (!API_BASE) {
    return `/${normalizedPath}`;
  }
  return `${API_BASE}/${normalizedPath}`;
};

type DataSourceResponse = DataSource[];

export function useDataSources(environmentId?: number | null) {
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { tokens } = useAuth();

  useEffect(() => {
    const resolvedEnvId =
      environmentId ??
      (typeof window !== "undefined" ? Number(getScopeEnvironmentId()) : null);
    if (!resolvedEnvId) {
      setError("Missing environment context");
      setLoading(false);
      return undefined;
    }
    let isMounted = true;
    const controller = new AbortController();
    const fetchDataSources = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          buildUrl(`environments/${resolvedEnvId}/data_sources`),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              ...(tokens.accessToken
                ? { Authorization: `Bearer ${tokens.accessToken}` }
                : {}),
            },
            signal: controller.signal
          }
        );
        if (!response.ok) {
          throw new Error(`Failed to load data sources (${response.status})`);
        }
        const payload: DataSourceResponse = await response.json();
        if (isMounted) {
          setDataSources(payload ?? []);
        }
      } catch (err) {
        if (!isMounted || controller.signal.aborted) {
          return;
        }
        const message = err instanceof Error ? err.message : "Unable to load data sources";
        setError(message);
        setDataSources([]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    void fetchDataSources();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [environmentId, tokens.accessToken]);

  const sourceEntitiesLookup = useMemo(() => {
    const lookup = new Map<number, SourceEntity[]>();
    dataSources.forEach((source) => {
      if (source.source_entity?.length) {
        lookup.set(source.data_source_id, source.source_entity);
      }
    });
    return lookup;
  }, [dataSources]);

  return {
    dataSources,
    sourceEntitiesLookup,
    loading,
    error
  };
}
